import AdminPageHeader from "@/components/admin/page-header";
import { useLoadFeedback } from "@/hooks/use-load-feedback";
import { PageState } from "@/components/ui/page-state";
import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Store, PowerOff, Power } from "lucide-react";
import api, { fileUrl, apiErrorMessage } from "@/lib/api";
import { useImageUpload, IMAGE_ACCEPT, UPLOAD_HINT } from "@/hooks/use-image-upload";
import { useOutlet } from "@/context/OutletContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const empty = {
  slug: "", name: "", tagline: "", phone: "", address: "", hours: "",
  logo_url: "", logo_file_id: "", primary_color: "#D14D36",
  tax_percent: 10, service_percent: 11, currency: "IDR",
  payment_note: "Pembayaran dilakukan di kasir setelah selesai makan.",
  qris_image_file_id: "", transfer_info: "",
  wa_notify_enabled: true, wa_notify_numbers: "",
  active: true,
};

export default function AdminOutlets() {
  const { loading, refreshing, hasLoaded, error: loadError, run } = useLoadFeedback();
  const { refresh } = useOutlet();
  const [outlets, setOutlets] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const { uploading: uploadingQris, upload } = useImageUpload();

  const load = useCallback(() => run(async () => {
    const { data } = await api.get("/outlets");
    setOutlets(data);
  }), [run]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (o) => {
    setEditing(o);
    setForm({ ...o });
    setOpen(true);
  };

  const handleQrisUpload = (e) =>
    upload(e, {
      folder: "qris",
      onUploaded: (id) => setForm((f) => ({ ...f, qris_image_file_id: id })),
      successMessage: "Gambar QRIS terupload",
    });

  const save = async () => {
    if (!form.name || !form.slug) return toast.error("Slug & Nama wajib diisi");
    const payload = {
      ...form,
      tax_percent: Number(form.tax_percent) || 0,
      service_percent: Number(form.service_percent) || 0,
    };
    try {
      if (editing) {
        await api.put(`/outlets/${editing.id}`, payload);
      } else {
        await api.post("/outlets", payload);
      }
      toast.success("Tersimpan");
      setOpen(false); load(); refresh();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Gagal menyimpan"));
    }
  };

  const toggleActive = async (o) => {
    try {
      await api.put(`/outlets/${o.id}`, { active: !o.active });
      toast.success(o.active ? "Outlet dinonaktifkan" : "Outlet diaktifkan");
      load(); refresh();
    } catch (err) { toast.error(apiErrorMessage(err, "Gagal mengubah status outlet")); }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus outlet? Ini akan menghilangkan meja dan referensi outlet.")) return;
    try {
      await api.delete(`/outlets/${id}`);
      toast.success("Outlet terhapus"); load(); refresh();
    } catch (err) { toast.error(apiErrorMessage(err, "Gagal hapus outlet")); }
  };

  if (loading || (loadError && !hasLoaded)) return <PageState kind={loading ? "loading" : "error"} title={loading ? "Memuat data" : "Data belum tersedia"} description={loadError} onRetry={loadError ? load : undefined} />;

  return (
    <div aria-busy={refreshing} className="admin-page space-y-6" data-testid="admin-outlets-page">
      {loadError && <PageState kind="error" title="Data belum dapat diperbarui" description={loadError} onRetry={load} />}
      <AdminPageHeader title="Kelola Outlet" description={<> Kelola semua cabang restoran </>} actions={<Button onClick={openNew} className="rounded-full" data-testid="add-outlet-btn">
          <Plus className="w-4 h-4 mr-1"/> <span className="hidden sm:inline">Tambah Outlet</span><span className="sm:hidden">Tambah</span>
        </Button>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {outlets.length === 0 && <PageState className="surface-card col-span-full" title="Belum ada outlet" description="Tambahkan outlet pertama untuk mulai mengelola cabang restoran." />}
        {outlets.map((o) => {
          const logo = o.logo_file_id ? fileUrl(o.logo_file_id) : o.logo_url;
          return (
            <div key={o.id} className="surface-card p-5" data-testid={`outlet-row-${o.slug}`}>
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-xl bg-accent border border-border overflow-hidden grid place-items-center flex-shrink-0">
                  {logo ? <img src={logo} alt="" className="w-full h-full object-contain p-1"/> : <Store className="w-6 h-6 text-primary"/>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display font-bold text-lg truncate">{o.name}</h3>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${o.active ? "bg-success/15 text-success" : "bg-muted text-muted-foreground"}`}>
                      {o.active ? "Aktif" : "Nonaktif"}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">{o.slug} • {o.tax_percent}% pajak • {o.service_percent}% service</p>
                  {o.address && <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{o.address}</p>}
                </div>
              </div>
              <div className="mt-4 flex gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={() => openEdit(o)} className="rounded-lg" data-testid={`edit-outlet-${o.slug}-btn`}>
                  <Pencil className="w-3.5 h-3.5 mr-1"/> Edit
                </Button>
                <Button variant="outline" size="sm" onClick={() => toggleActive(o)} className="rounded-lg" data-testid={`toggle-outlet-${o.slug}-btn`}>
                  {o.active ? <><PowerOff className="w-3.5 h-3.5 mr-1"/> Nonaktifkan</> : <><Power className="w-3.5 h-3.5 mr-1"/> Aktifkan</>}
                </Button>
                <Button variant="outline" size="sm" aria-label={`Hapus outlet ${o.name}`} onClick={() => remove(o.id)} className="rounded-lg text-destructive" data-testid={`delete-outlet-${o.slug}-btn`}>
                  <Trash2 className="w-3.5 h-3.5"/>
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="admin-dialog max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit" : "Tambah"} Outlet</DialogTitle>
            <DialogDescription>Setiap outlet punya identitas, tax, dan payment note sendiri. Menu tetap shared.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="adminoutlets-field-1">Slug (URL)</Label>
                <Input id="adminoutlets-field-1" value={form.slug} onChange={(e)=>setForm({...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-")})}
                       placeholder="tilamsari-solo" className="bg-white" data-testid="outlet-slug-input"/>
              </div>
              <div>
                <Label htmlFor="adminoutlets-field-2">Nama</Label>
                <Input id="adminoutlets-field-2" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} className="bg-white" data-testid="outlet-name-input"/>
              </div>
            </div>
            <div><Label htmlFor="adminoutlets-field-3">Tagline</Label><Input id="adminoutlets-field-3" value={form.tagline} onChange={(e)=>setForm({...form,tagline:e.target.value})} className="bg-white"/></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label htmlFor="adminoutlets-field-4">Telepon</Label><Input id="adminoutlets-field-4" value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} className="bg-white"/></div>
              <div>
                <Label htmlFor="adminoutlets-field-5">Jam Buka</Label>
                <Input id="adminoutlets-field-5" value={form.hours} onChange={(e)=>setForm({...form,hours:e.target.value})} placeholder="08:00 - 22:00" className="bg-white"/>
                <p className="text-[11px] text-muted-foreground mt-1">Format HH:MM - HH:MM. Outlet otomatis tutup di luar jam ini.</p>
              </div>
            </div>
            <div><Label htmlFor="adminoutlets-field-6">Alamat</Label><Textarea id="adminoutlets-field-6" rows={2} value={form.address} onChange={(e)=>setForm({...form,address:e.target.value})} className="bg-white"/></div>
            <div><Label htmlFor="adminoutlets-field-7">Logo URL (opsional)</Label><Input id="adminoutlets-field-7" value={form.logo_url} onChange={(e)=>setForm({...form,logo_url:e.target.value})} className="bg-white"/></div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><Label htmlFor="adminoutlets-field-8">Pajak (%)</Label><Input id="adminoutlets-field-8" type="number" step="0.01" value={form.tax_percent} onChange={(e)=>setForm({...form,tax_percent:e.target.value})} className="bg-white"/></div>
              <div><Label htmlFor="adminoutlets-field-9">Service (%)</Label><Input id="adminoutlets-field-9" type="number" step="0.01" value={form.service_percent} onChange={(e)=>setForm({...form,service_percent:e.target.value})} className="bg-white"/></div>
              <div><Label>Warna</Label>
                <div className="flex items-center gap-1">
                  <input aria-label="Pilih warna utama" type="color" value={form.primary_color} onChange={(e)=>setForm({...form,primary_color:e.target.value})}
                         className="w-9 h-9 rounded-lg border border-border cursor-pointer bg-white"/>
                  <Input aria-label="Kode warna utama" value={form.primary_color} onChange={(e)=>setForm({...form,primary_color:e.target.value})} className="bg-white text-xs"/>
                </div>
              </div>
            </div>
            <div><Label htmlFor="adminoutlets-field-10">Catatan Pembayaran</Label><Textarea id="adminoutlets-field-10" rows={2} value={form.payment_note} onChange={(e)=>setForm({...form,payment_note:e.target.value})} className="bg-white"/></div>
            <div className="space-y-2">
              <Label>QRIS — Gambar QR Statis (opsional)</Label>
              {form.qris_image_file_id ? (
                <div className="flex items-center gap-3" data-testid="qris-preview-box">
                  <img src={fileUrl(form.qris_image_file_id)} alt="QRIS" className="w-20 h-20 object-contain bg-white border border-border rounded-lg p-1" data-testid="qris-preview"/>
                  <Button variant="outline" size="sm" onClick={() => setForm({ ...form, qris_image_file_id: "" })} data-testid="qris-remove-btn">Hapus</Button>
                </div>
              ) : null}
              <label className="inline-flex items-center gap-2 text-xs font-medium border border-dashed border-border rounded-lg px-3 py-2 cursor-pointer hover:border-primary/50 bg-white">
                {uploadingQris ? "Uploading..." : (form.qris_image_file_id ? "Ganti Gambar QRIS" : "Upload Gambar QRIS")}
                <input type="file" accept={IMAGE_ACCEPT} className="sr-only" disabled={uploadingQris} onChange={handleQrisUpload} data-testid="qris-upload-input"/>
              </label>
              <p className="text-[11px] text-muted-foreground">{UPLOAD_HINT} &mdash; jika diisi, pelanggan dapat memilih metode bayar QRIS saat checkout. Verifikasi pembayaran tetap manual oleh kasir.</p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="adminoutlets-field-11">Info Transfer Bank (opsional)</Label>
              <Textarea id="adminoutlets-field-11" rows={2} value={form.transfer_info || ""} onChange={(e)=>setForm({...form,transfer_info:e.target.value})}
                        placeholder={"Contoh:\nBCA 1234567890 a.n. TilamSari Resto"} className="bg-white" data-testid="transfer-info-input"/>
              <p className="text-[11px] text-muted-foreground">Jika diisi, pelanggan dapat memilih metode bayar Transfer Bank saat checkout.</p>
            </div>
            <div className="space-y-2 border-t border-border pt-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <Label>Notifikasi WhatsApp Pesanan Baru</Label>
                  <p className="text-[11px] text-muted-foreground">Kirim otomatis ke WA staf setiap ada pesanan masuk.</p>
                </div>
                <Switch aria-label="Aktifkan notifikasi WhatsApp" checked={form.wa_notify_enabled !== false} onCheckedChange={(v)=>setForm({...form,wa_notify_enabled:v})}/>
              </div>
              <div className="space-y-1">
                <Label htmlFor="adminoutlets-field-12">Nomor WhatsApp Tujuan (pisahkan dengan koma)</Label>
                <Input id="adminoutlets-field-12" value={form.wa_notify_numbers || ""} onChange={(e)=>setForm({...form,wa_notify_numbers:e.target.value})}
                       placeholder="081234567890, 085712345678" className="bg-white" data-testid="wa-notify-numbers-input"/>
                <p className="text-[11px] text-muted-foreground">
                  Boleh beberapa nomor (kasir, dapur, manajer). Format bebas: 0812…, +62812…, atau 62812…
                  Nomor ini hanya terlihat oleh admin, tidak ditampilkan ke pelanggan.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="adminoutlets-field-13">Aktif</Label>
              <Switch id="adminoutlets-field-13" checked={!!form.active} onCheckedChange={(v)=>setForm({...form,active:v})}/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} data-testid="save-outlet-btn">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
